/**
 * src/modules/monetization/context/AdEngineProvider.tsx
 *
 * Privacy-First Native Ad Engine Provider with Tier Gating and Frequency Capping.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AdEngineContext, AdEngineContextType, AdPayload } from './AdEngineContext';
import { multiAccountVault } from '../../../shell/auth/multi-account-vault';

// Sample fallback direct sponsor campaigns
const DIRECT_SPONSOR_CAMPAIGNS: Record<string, AdPayload> = {
  'header-banner': {
    adId: 'ad_yubikey_01',
    slotId: 'header-banner',
    sponsorName: 'Yubico Security',
    headline: 'Hardware 2FA for Sovereign Nodes',
    bodyText: 'Protect your validator keys with FIDO2 hardware passkeys.',
    callToAction: 'Get YubiKey 5C',
    targetUrl: 'https://www.yubico.com',
    imageUrl: '/assets/products/yubikey_5c.svg',
  },
  'chat-sponsor': {
    adId: 'ad_apricorn_02',
    slotId: 'chat-sponsor',
    sponsorName: 'Apricorn Aegis',
    headline: 'Hardware Encrypted Mesh Backups',
    bodyText: 'PIN-authenticated portable SSDs for offline cold storage.',
    callToAction: 'View Hardware Vaults',
    targetUrl: 'https://apricorn.com',
    imageUrl: '/assets/products/apricorn_padlock.svg',
  },
  'rewarded-extra-life': {
    adId: 'ad_ubiquiti_03',
    slotId: 'rewarded-extra-life',
    sponsorName: 'Ubiquiti Networks',
    headline: 'AI Pro Sovereign Camera Hub',
    bodyText: 'Deploy local computer vision without cloud subscription fees.',
    callToAction: 'Claim Free Continue',
    targetUrl: 'https://ui.com',
    imageUrl: '/assets/products/ubiquiti_ai_pro.svg',
    isRewardable: true,
  },
};

export const AdEngineProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(() => multiAccountVault.getCurrentUser());
  const [lastMessageImpressionTime, setLastMessageImpressionTime] = useState<number>(0);

  useEffect(() => {
    const handleAuthChange = () => {
      setCurrentUser(multiAccountVault.getCurrentUser());
    };
    multiAccountVault.on('auth:stateChanged', handleAuthChange);
    multiAccountVault.on('auth:login', handleAuthChange);
    multiAccountVault.on('auth:logout', handleAuthChange);
    return () => {
      multiAccountVault.off('auth:stateChanged', handleAuthChange);
      multiAccountVault.off('auth:login', handleAuthChange);
      multiAccountVault.off('auth:logout', handleAuthChange);
    };
  }, []);

  // Paid, Pro, Founder, or Admin accounts are strictly ad-free
  const isAdFree = useMemo(() => {
    if (!currentUser) return false;
    const role = currentUser.role;
    return role === 'admin' || (role as string) === 'pro' || (role as string) === 'mesh_owner';
  }, [currentUser]);

  const requestNativeAd = useCallback(
    async (slotId: string): Promise<AdPayload | null> => {
      if (isAdFree) return null;

      // Frequency capping for chat/feed sponsored cards: max 1 per 4 hours (14400s)
      if (slotId === 'chat-sponsor') {
        const now = Date.now();
        if (now - lastMessageImpressionTime < 4 * 60 * 60 * 1000) {
          return null;
        }
      }

      // Try server endpoint first; fall back to local direct sponsor campaign
      try {
        const res = await fetch(`/api/v1/ads/serve?slot=${encodeURIComponent(slotId)}`, {
          signal: AbortSignal.timeout(2000),
        }).catch(() => null);
        if (res && res.ok) {
          const remoteAd: AdPayload = await res.json();
          if (remoteAd?.adId) return remoteAd;
        }
      } catch {}

      return DIRECT_SPONSOR_CAMPAIGNS[slotId] || null;
    },
    [isAdFree, lastMessageImpressionTime]
  );

  const recordImpression = useCallback((adId: string) => {
    if (adId.includes('chat')) {
      setLastMessageImpressionTime(Date.now());
    }
  }, []);

  const recordClick = useCallback((_adId: string) => {
    // Zero-tracking metrics (no UUIDs recorded)
  }, []);

  const requestRewardedAd = useCallback(
    async (placementId: string): Promise<boolean> => {
      if (isAdFree) return true; // Paid users automatically receive reward
      const ad = DIRECT_SPONSOR_CAMPAIGNS[placementId] || DIRECT_SPONSOR_CAMPAIGNS['rewarded-extra-life'];
      if (!ad) return true;
      return true;
    },
    [isAdFree]
  );

  const value: AdEngineContextType = useMemo(
    () => ({
      isAdFree,
      requestNativeAd,
      recordImpression,
      recordClick,
      requestRewardedAd,
    }),
    [isAdFree, requestNativeAd, recordImpression, recordClick, requestRewardedAd]
  );

  return <AdEngineContext.Provider value={value}>{children}</AdEngineContext.Provider>;
};
